import cn from 'classnames'
import type { FC } from 'react'
import { useEffect, useState } from 'react'

import CommunityHeader from '@components/project/CommunityHeader'
import emitter, { EVENT_MENU_OPEN } from '@lib/events'
import { getOrigin } from '@motif/shared/domain.edge'
import type { CommunitySiteViewProps } from '@motif/types'

const CommunitySiteView: FC<CommunitySiteViewProps> = ({
  title,
  domain,
  fullScreen,
  includeLink,
  AddButton,
}) => {
  const [eventsDisabled, setEventsDisabled] = useState(false)
  const [iframeDidLoad, setIframeDidLoad] = useState(false)
  const [iframeHeight, setIframeHeight] = useState(400)
  const [iframeMinHeight, setIframeMinHeight] = useState(400)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    setIframeMinHeight(window.innerHeight - 300)

    const handleResize = () => {
      setIframeMinHeight(window.innerHeight - 300)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (fullScreen) {
      return
    }

    const onMessage = async (event: MessageEvent) => {
      event.preventDefault()
      if (event.data?.name === 'window_height') {
        setIframeHeight(event.data.height + 200 || 10)
      }
    }

    window.addEventListener('message', onMessage)

    return () => {
      window.removeEventListener('message', onMessage)
    }
  }, [fullScreen])

  useEffect(() => {
    // Hack: click events from the iframe do not bubble to the parent.
    // So when opening a Menu in the app, if the iframe is clicked, you'd
    // expect it to close the menu, but this doesn't happen because the
    // click cannot be seen by the menu. So what we do is: when the menu is
    // opened, it emits an event (`EVENT_MENU_OPEN`) which disables pointer
    // events on the iframe, yielding the expected behavior.
    const handler = ({ open }: any) => {
      // We need to update the state with a small delay, otherwise
      // we have a render conflict error.
      setTimeout(() => {
        setEventsDisabled(open)
      }, 50)
    }
    emitter.on(EVENT_MENU_OPEN, handler)
    return () => {
      emitter.off(EVENT_MENU_OPEN, handler)
    }
  }, [setEventsDisabled])

  return (
    <div>
      {!fullScreen && (
        <div className="mx-auto max-w-screen-lg p-8">
          <CommunityHeader
            includeLink={includeLink}
            title={title || 'Untitled site'}
            ctaButton={AddButton}
          />
        </div>
      )}
      <iframe
        title={title}
        className={cn('absolute h-full w-full bg-white', {
          'pointer-events-none': eventsDisabled,
          'opacity-0': !fullScreen && !iframeDidLoad,
          'border-t border-neutral-100': !fullScreen,
        })}
        sandbox="allow-popups-to-escape-sandbox allow-scripts allow-popups allow-forms allow-pointer-lock allow-top-navigation allow-modals allow-same-origin"
        src={`${getOrigin(domain, true, 'brandless=1')}`}
        style={
          !fullScreen
            ? {
                minHeight: iframeMinHeight,
                height: iframeHeight,
              }
            : {}
        }
        onLoad={() => {
          !fullScreen && setIframeDidLoad(true)
        }}
      />
    </div>
  )
}

export default CommunitySiteView
